import { useState, useEffect, useCallback } from "react";
import "./DeducaoTable.css";
import { FaPlusCircle, FaEdit, FaTrashAlt, FaCheck } from "react-icons/fa";
import { formatDateBR } from "../../../../../utils/date";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface AtrasoDoc {
  id?: number;
  dataEnvio?: string;
  dataLimite?: string;
  dataVencimento?: string;
  diasAtraso?: number;
  percentualDeducao?: number;
  valorTotalApoio?: number;
  valorDeducao?: number;
  deduzir?: boolean;
}

export interface TituloEmAberto {
  id?: number | string;
  recno?: number;
  filial?: string;
  titulo?: string;
  parcela?: string;
  natureza?: string;
  dataEmissao?: string;
  dataVencimento?: string;
  atrasoDias?: number;
  valor?: number;
  historico?: string;
  deduzir?: boolean;
}

export interface OutraDeducao {
  id?: number;
  descricao: string;
  valor: number;
  deduzir: boolean;
  isNew?: boolean;
}

export interface DadosDeducao {
  deducaoOutros: OutraDeducao[];
  deducaoTitulos: TituloEmAberto[];
  deducaoAtraso: AtrasoDoc | Record<string, never>;
}

export interface ErroOutraDeducao {
  descricao: boolean;
  valor: boolean;
}

export const MSG_OUTRAS_DEDUCOES_INVALIDAS =
  "Preencha a descrição e o valor de todas as outras deduções.";

/** Cada linha de "Outras deduções" precisa de descrição e valor > 0. Erros indexados pela posição da linha. */
export function validarOutrasDeducoes(
  outras: OutraDeducao[] = [],
): Record<number, ErroOutraDeducao> {
  const erros: Record<number, ErroOutraDeducao> = {};
  outras.forEach((o, idx) => {
    const descricao = !String(o?.descricao ?? "").trim();
    const valor = !(Number(o?.valor) > 0);
    if (descricao || valor) erros[idx] = { descricao, valor };
  });
  return erros;
}

export const MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO =
  "A soma das outras deduções marcadas para deduzir não pode ser maior que o valor bruto do apoio.";

/** Soma das "Outras deduções" marcadas para deduzir. */
export function somarOutrasDeducoes(outras: OutraDeducao[] = []): number {
  return outras
    .filter((o) => o?.deduzir)
    .reduce((sum, o) => sum + (Number(o?.valor) || 0), 0);
}

/** true quando a soma das "Outras deduções" marcadas ultrapassa o valor bruto do apoio. */
export function outrasDeducoesExcedemBruto(
  outras: OutraDeducao[] = [],
  valorBruto: number | undefined | null,
): boolean {
  return somarOutrasDeducoes(outras) > (Number(valorBruto) || 0);
}

function toLocalDate(isoLike: string | undefined | null): Date | null {
  if (!isoLike) return null;
  const [y, m, d] = isoLike.split("T")[0].split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function calcAtrasoDias(dataVencimentoStr: string | undefined): number {
  const venc = toLocalDate(dataVencimentoStr ?? undefined);
  if (!venc) return 0;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const diff = Math.floor((hoje.getTime() - venc.getTime()) / MS_PER_DAY);
  return Math.max(0, diff);
}

export interface DeducaoTableProps {
  atrasoDocs?: AtrasoDoc[];
  titulosEmAberto?: TituloEmAberto[];
  outrasDeducoes?: OutraDeducao[];
  totalBrutoBonus?: string;
  onUpdateDeducoes?: (dados: DadosDeducao) => void;
  disabled?: boolean;
  initialTotalLiquido?: number;
  hideControls?: boolean;
  keepTotalFixed?: boolean;
  /** Destaca em vermelho as linhas de "Outras deduções" sem descrição ou sem valor (após tentativa de salvar). */
  mostrarErrosOutras?: boolean;
}

const DeducaoTable = ({
  atrasoDocs = [],
  titulosEmAberto = [],
  outrasDeducoes: outrasDeducoesProp = [],
  totalBrutoBonus = "R$0,00",
  onUpdateDeducoes = () => {},
  disabled = false,
  initialTotalLiquido,
  hideControls = false,
  keepTotalFixed = false,
  mostrarErrosOutras = false,
}: DeducaoTableProps) => {
  const [userInteracted, setUserInteracted] = useState(false);
  const [tentouConfirmarInvalida, setTentouConfirmarInvalida] = useState(false);
  const [outrasDeducoes, setOutrasDeducoes] = useState<OutraDeducao[]>([...outrasDeducoesProp]);

  const [atrasos, setAtrasos] = useState<AtrasoDoc[]>(
    [...atrasoDocs].sort(
      (a, b) =>
        new Date(a.dataVencimento ?? 0).getTime() -
        new Date(b.dataVencimento ?? 0).getTime(),
    ),
  );

  const [titulos, setTitulos] = useState<TituloEmAberto[]>(
    [...titulosEmAberto].sort(
      (a, b) =>
        new Date(a.dataVencimento ?? 0).getTime() -
        new Date(b.dataVencimento ?? 0).getTime(),
    ),
  );

  const atualizarNoPai = useCallback(
    (
      outras = outrasDeducoes,
      titulosAtuais = titulos,
      atrasosAtuais = atrasos,
    ) => {
      onUpdateDeducoes({
        deducaoOutros: outras,
        deducaoTitulos: titulosAtuais,
        deducaoAtraso: atrasosAtuais[0] || {},
      });
    },
    [onUpdateDeducoes, outrasDeducoes, titulos, atrasos],
  );

  useEffect(() => {
    atualizarNoPai();
  }, [outrasDeducoes, titulos, atrasos, atualizarNoPai]);

  const adicionarLinha = () => {
    setUserInteracted(true);
    const novaLinha = { descricao: "", valor: 0, deduzir: true, isNew: true };
    const novasOutras = [...outrasDeducoes, novaLinha];
    setOutrasDeducoes(novasOutras);
    atualizarNoPai(novasOutras);
  };

  const handleDeletar = (idx: number) => {
    setUserInteracted(true);
    const updated = outrasDeducoes.filter((_, i) => i !== idx);
    setOutrasDeducoes(updated);
    atualizarNoPai(updated);
  };

  const handleChange = (
    index: number,
    field: string,
    value: string | number | boolean,
  ) => {
    setUserInteracted(true);
    setOutrasDeducoes((prev) => {
      const updated = prev.map((item, idx) => {
        if (idx !== index) return item;

        if (field === "valor") {
          return {
            ...item,
            valor: typeof value === "number" ? value : Number(value) || 0,
          };
        }

        if (field === "deduzir") {
          return { ...item, deduzir: Boolean(value) };
        }

        return { ...item, [field]: value } as OutraDeducao;
      });

      atualizarNoPai(updated, titulos, atrasos);
      return updated;
    });
  };

  const errosOutras =
    mostrarErrosOutras || tentouConfirmarInvalida
      ? validarOutrasDeducoes(outrasDeducoes)
      : {};

  const handleConfirmar = (idx: number) => {
    if (validarOutrasDeducoes([outrasDeducoes[idx]])[0]) {
      setTentouConfirmarInvalida(true);
      return;
    }
    handleChange(idx, "isNew", false);
  };

  const calcularValor = (valor: string | number | undefined): number => {
    const str = valor?.toString() ?? "";
    const parsed = parseFloat(
      str.replace(/[R$\s.]/g, "").replace(",", "."),
    );
    return isNaN(parsed) ? 0 : parsed;
  };

  const formatar = (valor: number): string =>
    valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const totalAtraso = atrasos
    .filter((d) => d.deduzir)
    .reduce((sum, item) => sum + (item.valorDeducao || 0), 0);

  const totalTitulos = titulos
    .filter((d) => d.deduzir)
    .reduce((sum, item) => sum + (item.valor || 0), 0);

  const totalOutrasValor = somarOutrasDeducoes(outrasDeducoes);
  const outrasExcedemBruto =
    !hideControls && totalOutrasValor > calcularValor(totalBrutoBonus);

  const totalGeral = totalAtraso + totalTitulos + totalOutrasValor;

  const totalLiquido =
    (keepTotalFixed && initialTotalLiquido !== undefined) ||
    (!userInteracted && initialTotalLiquido !== undefined)
      ? initialTotalLiquido
      : calcularValor(totalBrutoBonus) - totalGeral;

  return (
    <div>
      <div className="deducao-section-title">DEDUÇÕES</div>

      <div className="deducao-subtitle">
        <span className="deducao-dot">•</span> Atraso entrega da documentação:
      </div>
      <div className="table-responsive">
        <table className="deducao-doc-table">
          <thead>
            <tr>
              <th>DATA ENVIO DOC.</th>
              <th>DATA LIMITE</th>
              <th>DIAS DE ATRASO</th>
              <th>% DEDUÇÃO</th>
              <th>VALOR TOTAL DO APOIO</th>
              <th>VALOR A DEDUZIR</th>
              {!hideControls && <th className="deduzir-header">DEDUZIR</th>}
            </tr>
          </thead>
          <tbody>
            {atrasos.map((item, idx) => (
              <tr key={idx} data-pdf-filter={item.deduzir ? "true" : "false"}>
                <td>{formatDateBR(item.dataEnvio)}</td>
                <td>{formatDateBR(item.dataLimite ?? item.dataVencimento)}</td>
                <td>{item.diasAtraso}</td>
                <td>{item.percentualDeducao}%</td>
                <td>{formatar(item.valorTotalApoio || 0)}</td>
                <td>{formatar(item.valorDeducao || 0)}</td>
                {!hideControls && (
                  <td className="deduzir-cell">
                    <input
                      type="checkbox"
                      checked={item.deduzir}
                      disabled={disabled}
                      onChange={(e) => {
                        setUserInteracted(true);
                        const updated = [...atrasos];
                        updated[idx].deduzir = e.target.checked;
                        setAtrasos(updated);
                        atualizarNoPai(outrasDeducoes, titulos, updated);
                      }}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="deducao-subtitle">
        <span className="deducao-dot">•</span> Títulos em aberto:
      </div>
      <div className="table-responsive">
        <table className="deducao-doc-table">
          <thead>
            <tr>
              <th>FILIAL</th>
              <th>Nº TÍTULO</th>
              <th className="parcela-cell">PARCELA</th>
              <th>NATUREZA</th>
              <th>EMISSÃO</th>
              <th>VENCIMENTO</th>
              <th>ATRASO (dias)</th>
              <th>VALOR</th>
              <th>HISTÓRICO</th>
              {!hideControls && <th className="deduzir-header">DEDUZIR</th>}
            </tr>
          </thead>
          <tbody>
            {titulos.map((item, idx) => (
              <tr key={idx} data-pdf-filter={item.deduzir ? "true" : "false"}>
                <td>{item.filial}</td>
                <td>{item.titulo}</td>
                <td className="parcela-cell">{item.parcela || "-"}</td>
                <td>{item.natureza}</td>
                <td>{formatDateBR(item.dataEmissao)}</td>
                <td>{formatDateBR(item.dataVencimento)}</td>
                <td>{calcAtrasoDias(item.dataVencimento)}</td>
                <td>{formatar(item.valor || 0)}</td>
                <td className="historico-cell" title={item.historico || undefined}>
                  {item.historico || "-"}
                </td>
                {!hideControls && (
                  <td className="deduzir-cell">
                    <input
                      type="checkbox"
                      checked={item.deduzir}
                      disabled={disabled}
                      onChange={(e) => {
                        setUserInteracted(true);
                        const updated = [...titulos];
                        updated[idx].deduzir = e.target.checked;
                        setTitulos(updated);
                        atualizarNoPai(outrasDeducoes, updated, atrasos);
                      }}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={hideControls ? 9 : 10} className="total-label">
                VALOR TOTAL A DEDUZIR:{" "}
                <span className="valor-vermelho">{formatar(totalTitulos)}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="deducao-subtitle">
        <span className="deducao-dot">•</span> Outras deduções:
      </div>
      <div className="table-responsive">
        <table className="deducao-doc-table outras-deducoes-tabela">
          <thead>
            <tr>
              <th>DESCRIÇÃO</th>
              <th>VALOR</th>
              {!hideControls && <th className="deduzir-header">DEDUZIR</th>}
              {!hideControls && <th className="acao-header">AÇÃO</th>}
            </tr>
          </thead>
          <tbody>
            {outrasDeducoes.map((item, idx) => {
              const erro = errosOutras[idx];
              // Linha inválida fica editável para o usuário corrigir sem precisar clicar em Editar.
              const bloqueada = !item.isNew && !erro;
              return (
                <tr key={idx} data-pdf-filter={item.deduzir ? "true" : "false"}>
                  <td>
                    <input
                      type="text"
                      value={item.descricao}
                      disabled={bloqueada}
                      onChange={(e) =>
                        handleChange(idx, "descricao", e.target.value)
                      }
                      className={`table-input ${erro?.descricao ? "is-invalid" : ""}`}
                      aria-label="Descrição da dedução"
                      aria-invalid={erro?.descricao || undefined}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatar(item.valor)}
                      disabled={bloqueada}
                      onChange={(e) => {
                        const digitos = e.target.value.replace(/\D/g, "");
                        const numero = digitos ? Number(digitos) / 100 : 0;
                        handleChange(idx, "valor", numero);
                      }}
                      className={`table-input ${erro?.valor ? "is-invalid" : ""}`}
                      aria-label="Valor da dedução"
                      aria-invalid={erro?.valor || undefined}
                    />
                  </td>
                  {!hideControls && (
                    <td className="deduzir-cell">
                      <input
                        type="checkbox"
                        checked={item.deduzir}
                        disabled={disabled}
                        onChange={(e) =>
                          handleChange(idx, "deduzir", e.target.checked)
                        }
                      />
                    </td>
                  )}
                  {!hideControls && (
                    <td className="acao-cell">
                      {item.isNew ? (
                        <button
                          className="acao-btn acao-btn--confirm"
                          title="Confirmar"
                          disabled={disabled}
                          onClick={() => handleConfirmar(idx)}
                        >
                          <FaCheck />
                        </button>
                      ) : (
                        <button
                          className="acao-btn acao-btn--edit"
                          title="Editar"
                          disabled={disabled}
                          onClick={() => handleChange(idx, "isNew", true)}
                        >
                          <FaEdit />
                        </button>
                      )}
                      <button
                        className="acao-btn acao-btn--delete"
                        title="Excluir"
                        disabled={disabled}
                        onClick={() => handleDeletar(idx)}
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={hideControls ? 2 : 4} className="total-label">
                VALOR TOTAL A DEDUZIR:{" "}
                <span className="valor-vermelho">{formatar(totalOutrasValor)}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {outrasExcedemBruto && (
        <div className="outras-deducoes-erro" role="alert">
          {MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO}
        </div>
      )}

      {!hideControls && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="adicionar-linha-btn" onClick={adicionarLinha} disabled={disabled}>
            <FaPlusCircle className="plus-icon" /> ADICIONAR LINHA
          </button>
        </div>
      )}

      <div className="table-responsive">
        <table className="deducao-totalizador-table">
          <tbody>
            <tr>
              <td>ATRASO ENTREGA DA DOCUMENTAÇÃO</td>
              <td>{formatar(totalAtraso)}</td>
            </tr>
            <tr>
              <td>TÍTULOS EM ABERTO</td>
              <td>{formatar(totalTitulos)}</td>
            </tr>
            <tr>
              <td>OUTRAS DEDUÇÕES</td>
              <td>{formatar(totalOutrasValor)}</td>
            </tr>
            <tr className="last-row">
              <td className="ultimo-valor">VALOR TOTAL DAS DEDUÇÕES</td>
              <td className="valor-vermelho">{formatar(totalGeral)}</td>
            </tr>
          </tbody>
        </table>

        <div className="total-liquido-apoio">
          <div className="total-liquido-apoio-label">
            TOTAL LÍQUIDO DO APOIO
          </div>
          <div className="total-liquido-apoio-valor">
            {formatar(totalLiquido)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeducaoTable;
